import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Form, Formik } from 'formik';

import Button from 'components/Common/Button/Button';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import Modal from 'components/Common/Modal/Modal';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import Trans from 'components/Common/Translation/Trans';
import Loading from 'components/Common/Loading/Loading';
import { validationSchemaResetPassword } from 'schemas/SignInWithEmailErrors';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { GTM_FORM_FORGOT_CUSTOMER_NUMBER } from 'settings/gtm/gtmForm';
import { GTM_PAGE_ACCOUNT } from 'settings/gtm/gtmPageView';

import styles from './ModalForgotCustomerNumber.module.scss';

type FormType = {
  email: string;
  turnstileToken: string;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
};

export default function ModalForgotCustomerNumber({
  isOpen,
  closeModal,
}: Props): JSX.Element | null {
  const { t } = useTranslation(['loginPage']);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const turnstile = useRef<TurnstileRef>();
  const isTurnstileDesktop = useMediaQuery({ minWidth: '380px' });
  const [errorMessages, setErrorMessages] = useState<
    Record<string, string | React.ReactElement>
  >();

  const extraStyle: ExtraStyleType = {
    container: styles.container,
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  async function onSubmit(values: FormType) {
    setLoading(true);
    setErrorMessages(undefined);

    turnstile.current?.reset();

    const uri = apiUrls.getCustomerForgotNumber();
    const response = (await fetcherWithBody(uri, values, true)) as Response;

    if (response.ok) {
      setSubmitted(true);
      setLoading(false);

      return;
    }

    const json = await response.json();

    if (json.error) {
      setErrorMessages({ global: json.error });
    } else if (json.errors && Object.keys(json.errors).length > 0) {
      setErrorMessages(json.errors);
    }

    setLoading(false);
  }

  useEffect(() => {
    return () => {
      document.body.style.removeProperty('overflow-y');
      document.body.style.removeProperty('height');
    };
  }, []);

  if (!isOpen) {
    return null;
  }

  const closeModalFunction = () => {
    setSubmitted(false);
    closeModal();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        modalClassName={styles.popupmodal}
        appendInBody={true}
      >
        <header className={styles.modalheader}>
          <h1 className={styles.modaltitle}>
            <Trans
              i18nKey={'FORGOT_CUSTOMER_NUMBER_MODAL_TITLE'}
              namespace={'loginPage'}
            />
          </h1>
          <button
            className={styles.actionclose}
            type="button"
            onClick={closeModalFunction}
            aria-label={t('LABEL_CLOSE')}
          >
            <span className={styles.closeIcon} />
          </button>
        </header>
        <div className={styles.modalcontent}>
          {!submitted && (
            <Formik
              initialValues={{ email: '', turnstileToken: '' }}
              validationSchema={validationSchemaResetPassword}
              onSubmit={onSubmit}
            >
              <Form className={styles.formforgetcustomernb} noValidate>
                <FormikErrorsGtmLogger
                  page_type={GTM_PAGE_ACCOUNT}
                  form_type={GTM_FORM_FORGOT_CUSTOMER_NUMBER}
                />
                <fieldset className={styles.fieldset}>
                  <div className={styles.textArea}>
                    <Trans
                      i18nKey={'FORGOT_CUSTOMER_NUMBER_MODAL_WHERE_FIND_IT'}
                      namespace={'loginPage'}
                    />
                  </div>
                  <div className={styles.textArea}>
                    <Trans
                      i18nKey={'FORGOT_CUSTOMER_NUMBER_MODAL_ASK_BY_EMAIL'}
                      namespace={'loginPage'}
                    />
                  </div>
                </fieldset>
                <TextFieldCustom
                  label={t('LABEL_MY_EMAIL_ADDRESS')}
                  name="email"
                  required
                  extraStyle={extraStyle}
                  placeholder={t('LABEL_PLACEHOLDER_EMAIL')}
                />
                <TurnstileFormikRow
                  classes={{
                    row: styles.container,
                    errorMessage: styles.captchaErrorMessage,
                  }}
                  turnstileRef={turnstile}
                  turnstileProps={{
                    id: 'turnstile-forgot-customer-number',
                    className: styles.captchaField,
                    options: {
                      action: 'forgot_customer_number',
                      size: isTurnstileDesktop ? 'normal' : 'compact',
                    },
                  }}
                />
                {Object.keys(errorMessages || {}).length > 0 && (
                  <div className={styles.formErrorContainer}>
                    {Object.values(errorMessages || {}).map(
                      (errorMessage, key) => (
                        <div className={styles.formError} key={key}>
                          {errorMessage}
                        </div>
                      )
                    )}
                  </div>
                )}
                <div className={styles.actionstoolbar}>
                  <Button>{t('LABEL_SEND_CUSTOMER_NUMBER')}</Button>
                </div>
                <RequiredFields
                  classes={{ wrapper: styles.requiredFieldsInfo }}
                />
                <ScrollToFieldError />
              </Form>
            </Formik>
          )}
          {submitted && (
            <div className={styles.fieldset}>
              <div className={styles.textArea}>
                <Trans
                  i18nKey={'LABEL_RECEIVED_REQUEST_CUSTOMER_NUMBER'}
                  namespace={'loginPage'}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
      {isLoading && <Loading loading />}
    </>
  );
}
